import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { safeNext } from "./lib/auth";
import { parseSearch, parseSearchString, stringifySearch } from "./lib/search";
import { AdminIngestPage } from "./pages/AdminIngestPage";
import { DataCentersPage } from "./pages/DataCentersPage";
import { ListDetailPage } from "./pages/ListDetailPage";
import { ListsIndexPage } from "./pages/ListsIndexPage";
import { LoginPage } from "./pages/LoginPage";
import { MapPage } from "./pages/MapPage";
import { ReviewPage } from "./pages/ReviewPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SitePage } from "./pages/SitePage";
import { TablePage } from "./pages/TablePage";
import { RouteError } from "./ui/RouteError";
import { Shell } from "./ui/Shell";

/** Every page sits in the app shell except the login page, which stands on its own. */
function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname === "/login") return <Outlet />;
  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}

const rootRoute = createRootRoute({ component: RootLayout });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  // /login?next=/table keeps the page the visitor asked for; only same-origin paths are accepted
  validateSearch: (raw: Record<string, unknown>): { next: string } => ({
    next: safeNext(raw.next),
  }),
  component: LoginPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: parseSearch,
  component: MapPage,
});

const siteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sites/$id",
  validateSearch: parseSearch,
  component: SitePage,
});

const tableRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/table",
  validateSearch: parseSearch,
  component: TablePage,
});

const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/review",
  // /review?site_id=123 opens the queue filtered to one site (linked from the site overview)
  validateSearch: (raw: Record<string, unknown>): { site_id?: number } => {
    const n = Number(raw.site_id);
    return Number.isInteger(n) && n > 0 ? { site_id: n } : {};
  },
  component: ReviewPage,
});

const listsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lists",
  component: ListsIndexPage,
});

const listDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lists/$id",
  component: ListDetailPage,
});

const dcRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dc",
  component: DataCentersPage,
});

// Explore pages (ADR-008) load on demand so the map route does not pay for the chart code or d3-geo.
const chartsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/charts",
  component: lazyRouteComponent(() => import("./pages/ChartsIndexPage"), "ChartsIndexPage"),
});

const chartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/charts/$slug",
  component: lazyRouteComponent(() => import("./pages/ChartPage"), "ChartPage"),
});

const exploreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/explore",
  component: lazyRouteComponent(() => import("./pages/ExploreIndexPage"), "ExploreIndexPage"),
});

const topicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/explore/$topic",
  component: lazyRouteComponent(() => import("./pages/TopicPage"), "TopicPage"),
});

const statesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/states",
  component: lazyRouteComponent(() => import("./pages/StatesPage"), "StatesPage"),
});

const stateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/states/$st",
  component: lazyRouteComponent(() => import("./pages/StatePage"), "StatePage"),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsPage,
});

const adminIngestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/ingest",
  component: AdminIngestPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  siteRoute,
  tableRoute,
  reviewRoute,
  listsRoute,
  listDetailRoute,
  dcRoute,
  chartsRoute,
  chartRoute,
  exploreRoute,
  topicRoute,
  statesRoute,
  stateRoute,
  settingsRoute,
  adminIngestRoute,
]);

export const router = createRouter({
  routeTree,
  parseSearch: parseSearchString,
  stringifySearch,
  defaultPreload: "intent",
  defaultErrorComponent: RouteError,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export {
  adminIngestRoute,
  chartRoute,
  chartsRoute,
  dcRoute,
  exploreRoute,
  indexRoute,
  listDetailRoute,
  listsRoute,
  loginRoute,
  reviewRoute,
  settingsRoute,
  siteRoute,
  stateRoute,
  statesRoute,
  tableRoute,
  topicRoute,
};
